/**
 * Client-side batch search for autocomplete suggestions.
 * Fetches products (watches + straps) and stores in parallel.
 */

import type { GroupedHits, GroupedStores } from '@/components/search/SearchDropdown';
import { getLocaleCode } from '@/lib/algolia/config';
import type { StoreHit } from '@/lib/algolia/stores';
import type { ProductHit } from '@/lib/algolia/types';
import { BRAND, BRAND_ENUM } from '@/lib/cms/config';
import {
  AUTOCOMPLETE_FETCH_BUFFER,
  AUTOCOMPLETE_SUGGESTIONS_PER_TYPE,
  buildWatchAutocompleteSuggestions,
  dedupeProductHitsForAutocomplete,
  dedupeStoreHitsForAutocomplete,
} from '@/lib/search/dedupeAutocompleteHits';

export interface BatchSearchSuggestionsResult {
  groupedHits: GroupedHits[];
  groupedStores: GroupedStores | undefined;
}

export async function fetchBatchSearch(
  lang: string,
  searchQuery: string
): Promise<BatchSearchSuggestionsResult> {
  const countryCode = lang.split('-')[0].toUpperCase();
  const localeCode = getLocaleCode(lang);

  const [productsResponse, storesResponse] = await Promise.all([
    fetch('/api/products/batch-search', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        locale: lang,
        queries: [
          { productType: 'Strap', query: searchQuery, hitsPerPage: AUTOCOMPLETE_FETCH_BUFFER },
          { productType: 'Watch', query: searchQuery, hitsPerPage: AUTOCOMPLETE_FETCH_BUFFER },
        ],
      }),
    }),
    fetch(
      `/api/stores?country=${countryCode}&limit=${AUTOCOMPLETE_FETCH_BUFFER}&mode=Search&q=${encodeURIComponent(searchQuery)}`
    ),
  ]);

  let groupedHits: GroupedHits[] = [];

  if (productsResponse.ok) {
    const json = await productsResponse.json();
    const batchResults = json.data?.results || [];

    groupedHits = batchResults.map(
      (r: {
        productType: string;
        hits: ProductHit[];
        totalHits: number;
        facets?: { collection?: Record<string, number> };
      }) => ({
        type: r.productType as 'Watch' | 'Strap' | 'Eyewear',
        hits:
          r.productType === 'Watch'
            ? buildWatchAutocompleteSuggestions(
                r.hits,
                r.facets?.collection,
                localeCode,
                AUTOCOMPLETE_SUGGESTIONS_PER_TYPE,
                searchQuery,
                BRAND === BRAND_ENUM.Gallet
              )
            : dedupeProductHitsForAutocomplete(
                r.hits,
                localeCode,
                AUTOCOMPLETE_SUGGESTIONS_PER_TYPE
              ),
        totalCount: r.totalHits,
      })
    );
  }

  let groupedStores: GroupedStores | undefined;

  if (storesResponse.ok) {
    const json = await storesResponse.json();
    const stores = json.data?.stores || [];
    const totalHits = json.data?.totalHits || stores.length;

    if (stores.length > 0) {
      groupedStores = {
        type: 'Store',
        hits: dedupeStoreHitsForAutocomplete(
          stores as StoreHit[],
          AUTOCOMPLETE_SUGGESTIONS_PER_TYPE
        ),
        totalCount: totalHits,
      };
    }
  }

  return { groupedHits, groupedStores };
}
